import { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Button, Select, Option,
    IconButton, Input, Modal, ModalDialog, Switch, CircularProgress,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon       from '@mui/icons-material/ArrowBackRounded';
import SettingsRoundedIcon        from '@mui/icons-material/SettingsRounded';
import WarningAmberRoundedIcon    from '@mui/icons-material/WarningAmberRounded';
import DeleteForeverRoundedIcon   from '@mui/icons-material/DeleteForeverRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import VolumeOffRoundedIcon       from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon        from '@mui/icons-material/VolumeUpRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIClearMonth, parseApiError, APIGetAvailableMonths,
    APIGetMySilentKinds, APISetMySilentKinds, APISetMyMutedKinds,
    APIGetStudioSettings, APIUploadStudioQr, APIDeleteStudioQr
} from '../../api';

// ─── Человекочитаемые названия типов push ────────────────────────────────────
const PUSH_KIND_LABELS = {
    gcoin_manual:          'G coin: ручные операции',
    gcoin_revenue:         'G coin: часовая сводка',
    gcoin_win:             'G coin: победы ЛФН / ЛАН',
    audit:                 'Действия сотрудников (журнал)',
    assignment:            'Назначения / графики',
    assignment_changes:    'Изменения в сменах',
    fine:                  'Штрафы и списания',
    reward:                'Премии',
    sale_notification:     'Уведомления о продажах',
    payment_reminder:      'Напоминания об оплате',
    motivation:            'Мотивационные сообщения',
    schedule_reminder:     'Напоминание заполнить график',
    morning_reminder:      'Утренние напоминания',
    manager_daily:         'Ежедневная сводка руководителя',
    finance_summary:       'Финансовая сводка',
    change_request:        'Запросы на изменение',
    fill_request:          'Заявки на заполнение',
    substitution_request:  'Заявки на замену',
    substitution_taken:    'Замена найдена',
    substitution_broadcast:'Рассылка «нужна замена»',
};

// ─── Утилиты ──────────────────────────────────────────────────────────────────
// Красивое имя месяца: (2026, 7) → «Июль 2026 г.»
const formatMonthLabel = (year, month) => {
    const d = new Date(year, month - 1, 1);
    const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

// Ответ /available-months/ → опции для Select (только реальные месяцы).
const mapMonthsToOptions = (months = []) =>
    months.map(({ year, month }) => ({
        year,
        month,
        value: `${year}-${String(month).padStart(2, '0')}`,
        label: formatMonthLabel(year, month),
    }));

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

// ════════════════════════════════════════════════════════════════════
//  SettingsPage (менеджер)
// ════════════════════════════════════════════════════════════════════
export default function SettingsPage() {
    const { token, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canClear = hasPerm('finance.clear_month');

    // ── Очистка данных ────────────────────────────────────────────
    // Месяцы теперь берём с сервера — только те, за которые есть данные.
    const [monthOptions,    setMonthOptions]    = useState([]);
    const [monthsLoading,   setMonthsLoading]   = useState(true);
    const [clearMonth,      setClearMonth]      = useState('');
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [clearConfirmText, setClearConfirmText] = useState('');
    const [clearing,         setClearing]         = useState(false);

    const selectedMonthLabel = monthOptions.find(m => m.value === clearMonth)?.label || '';

    // Строка подтверждения — пользователь должен ввести название месяца
    const confirmMatch = clearConfirmText.trim().toLowerCase() === selectedMonthLabel.toLowerCase();

    // ── Настройки звука push ──────────────────────────────────────
    const [silentKinds,    setSilentKinds]    = useState([]);     // тихие у текущего пользователя
    const [mutedKinds,     setMutedKinds]     = useState([]);     // полностью отключённые
    const [availableKinds, setAvailableKinds] = useState([]);     // что вообще доступно по роли
    const [loadingSilent,  setLoadingSilent]  = useState(true);
    const [savingSilent,   setSavingSilent]   = useState(false);
    const [savingMuted,    setSavingMuted]    = useState(false);

    const [qrUrl, setQrUrl] = useState(null);
    const [qrBusy, setQrBusy] = useState(false);


    const loadSilentKinds = useCallback(async () => {
        if (!token) return;
        setLoadingSilent(true);
        const data = await APIGetMySilentKinds(token);
        setSilentKinds(data?.push_silent_kinds || []);
        setMutedKinds(data?.push_muted_kinds || []);
        setAvailableKinds(data?.available_kinds || []);
        setLoadingSilent(false);
    }, [token]);

    useEffect(() => { loadSilentKinds(); }, [loadSilentKinds]);

    // Переключатель: при тумблере оптимистично обновляем UI + шлём на сервер
    const toggleSilent = useCallback(async (kind, makeSilent) => {
        const next = makeSilent
            ? [...new Set([...silentKinds, kind])]
            : silentKinds.filter(k => k !== kind);
        setSilentKinds(next);                  // оптимистично
        setSavingSilent(true);
        const res = await APISetMySilentKinds(token, next);
        setSavingSilent(false);
        if (res?.error) {
            // Откатываем
            setSilentKinds(silentKinds);
            showToast(parseApiError(res.error, 'Не удалось сохранить'), 'danger');
        } else {
            setSilentKinds(res.push_silent_kinds || next);
        }
    }, [silentKinds, token, showToast]);

    // Переключатель полного отключения типа push
    const toggleMuted = useCallback(async (kind, makeMuted) => {
        const next = makeMuted
            ? [...new Set([...mutedKinds, kind])]
            : mutedKinds.filter(k => k !== kind);
        setMutedKinds(next);                   // оптимистично
        setSavingMuted(true);
        const res = await APISetMyMutedKinds(token, next);
        setSavingMuted(false);
        if (res?.error) {
            setMutedKinds(mutedKinds);         // откат
            showToast(parseApiError(res.error, 'Не удалось сохранить'), 'danger');
        } else {
            setMutedKinds(res.push_muted_kinds || next);
        }
    }, [mutedKinds, token, showToast]);

    useEffect(() => {
        (async () => {
            const data = await APIGetStudioSettings(token);
            setQrUrl(data?.qr_url || null);
        })();
    }, [token]);

    // Месяцы, за которые есть данные; по умолчанию выбираем самый свежий.
    useEffect(() => {
        if (!token) return;
        (async () => {
            setMonthsLoading(true);
            const data = await APIGetAvailableMonths(token);
            const opts = mapMonthsToOptions(data?.months || []);
            setMonthOptions(opts);
            setClearMonth(prev => prev || opts[0]?.value || '');
            setMonthsLoading(false);
        })();
    }, [token]);

    const handleQrUpload = async (file) => {
        if (!file) return;
        setQrBusy(true);
        const res = await APIUploadStudioQr(token, file);
        setQrBusy(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось загрузить QR'), 'danger'); return; }
        showToast('QR-код загружен ✓');
        setQrUrl(res.qr_url);
    };

    const handleQrDelete = async () => {
        setQrBusy(true);
        const res = await APIDeleteStudioQr(token);
        setQrBusy(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось удалить QR'), 'danger'); return; }
        setQrUrl(null);
    };

    const handleClear = async () => {
        if (!confirmMatch) return;

        setClearing(true);
        const selected = monthOptions.find(m => m.value === clearMonth);
        if (!selected) { setClearing(false); return; }

        const res = await APIClearMonth(token, {
            year:  selected.year,
            month: selected.month,
            confirm: true,
        });

        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка очистки'), 'danger');
        } else {
            const deleted = res?.deleted || {};
            const parts = [];
            if (deleted.orders)        parts.push(`${deleted.orders} чеков`);
            if (deleted.rewards)       parts.push(`${deleted.rewards} премий`);
            if (deleted.daily_rewards) parts.push(`${deleted.daily_rewards} ежедн. премий`);
            if (deleted.fines)         parts.push(`${deleted.fines} штрафов`);
            if (deleted.admin_salaries) parts.push(`${deleted.admin_salaries} окладов`);

            showToast(
                parts.length > 0
                    ? `Удалено за ${selectedMonthLabel}: ${parts.join(', ')}`
                    : `Данные за ${selectedMonthLabel} очищены`,
                'warning',
            );
            setClearConfirmOpen(false);
            setClearConfirmText('');
        }
        setClearing(false);
    };

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Настройки
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Управление системой
                        </Typography>
                    </Box>
                </Box>

                <Stack spacing={3}>

                    {/* ════════════════════════════════════════════ */}
                    {/*  Блок: Звук уведомлений                    */}
                    {/* ════════════════════════════════════════════ */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.02 }}>
                        <Box sx={{
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            overflow: 'hidden',
                            bgcolor: 'var(--joy-palette-background-level1)',
                        }}>
                            {/* Заголовок */}
                            <Box sx={{
                                px: 2.5, py: 1.5,
                                bgcolor: 'rgba(162,38,226,0.06)',
                                borderBottom: '1px solid var(--joy-palette-divider)',
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}>
                                <NotificationsActiveRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                                    Звук уведомлений
                                </Typography>
                            </Box>

                            {/* Описание */}
                            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2, lineHeight: 1.6 }}>
                                    Включи тумблер у тех типов уведомлений, которые должны приходить
                                    <strong> беззвучно</strong> (без вибрации и звука). 
                                    Остальные — с обычным звуком.
                                </Typography>

                                {loadingSilent ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                ) : availableKinds.length === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 2 }}>
                                        Для твоей роли нет настраиваемых типов уведомлений.
                                    </Typography>
                                ) : (
                                    <Stack spacing={0} divider={
                                        <Box sx={{ height: '1px', bgcolor: 'var(--joy-palette-divider)' }} />
                                    }>
                                        {availableKinds.map(kind => {
                                            const isSilent = silentKinds.includes(kind);
                                            const label    = PUSH_KIND_LABELS[kind] || kind;
                                            return (
                                                <Box key={kind} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    py: 1.2,
                                                }}>
                                                    {isSilent
                                                        ? <VolumeOffRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />
                                                        : <VolumeUpRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{
                                                            fontSize: 13, fontWeight: 600,
                                                            color: 'var(--joy-palette-text-primary)',
                                                        }}>
                                                            {label}
                                                        </Typography>
                                                        <Typography sx={{
                                                            fontSize: 11,
                                                            color: isSilent ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                                                        }}>
                                                            {isSilent ? 'Без звука' : 'Со звуком'}
                                                        </Typography>
                                                    </Box>
                                                    <Switch
                                                        size="sm"
                                                        checked={isSilent}
                                                        disabled={savingSilent}
                                                        onChange={(e) => toggleSilent(kind, e.target.checked)}
                                                        color={isSilent ? 'warning' : 'neutral'}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    </motion.div>

                    {/* ════════════════════════════════════════════ */}
                    {/*  Блок: Какие уведомления получать          */}
                    {/* ════════════════════════════════════════════ */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.03 }}>
                        <Box sx={{
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            overflow: 'hidden',
                            bgcolor: 'var(--joy-palette-background-level1)',
                        }}>
                            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    Какие уведомления получать
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    Выключенные типы не приходят вообще
                                </Typography>
                            </Box>
                            <Box sx={{ px: 2.5, py: 1 }}>
                                {loadingSilent ? (
                                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                ) : availableKinds.length === 0 ? (
                                    <Typography sx={{ py: 2, fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Нет доступных типов
                                    </Typography>
                                ) : (
                                    <Stack spacing={0} divider={<Box sx={{ height: '1px', bgcolor: 'var(--joy-palette-divider)' }} />}>
                                        {availableKinds.map(kind => {
                                            const isMuted = mutedKinds.includes(kind);
                                            const label   = PUSH_KIND_LABELS[kind] || kind;
                                            return (
                                                <Box key={kind} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                                            {label}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: isMuted ? '#ef4444' : 'var(--joy-palette-text-tertiary)' }}>
                                                            {isMuted ? 'Выключено' : 'Приходит'}
                                                        </Typography>
                                                    </Box>
                                                    <Switch
                                                        size="sm"
                                                        checked={!isMuted}
                                                        disabled={savingMuted}
                                                        onChange={(e) => toggleMuted(kind, !e.target.checked)}
                                                        color={isMuted ? 'danger' : 'success'}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)', p: 2.5, mb: 2,
                        }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>QR-код для клиентов</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                                Фотографы показывают его клиентам. PNG/JPG/WebP/SVG, до 5 МБ.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                {qrUrl && (
                                    <Box component="img" src={qrUrl} alt="QR"
                                        sx={{ width: 96, height: 96, objectFit: 'contain', bgcolor: '#fff', borderRadius: 'var(--joy-radius-md)', p: 0.5 }} />
                                )}
                                <Button component="label" variant="soft" loading={qrBusy} sx={{ fontWeight: 700 }}>
                                    {qrUrl ? 'Заменить' : 'Загрузить'}
                                    <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                        onChange={(e) => { handleQrUpload(e.target.files?.[0]); e.target.value = ''; }} />
                                </Button>
                                {qrUrl && (
                                    <Button variant="soft" color="danger" onClick={handleQrDelete} loading={qrBusy} sx={{ fontWeight: 700 }}>
                                        Удалить
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </motion.div>

                    {/* ════════════════════════════════════════════ */}
                    {/*  Блок: Опасная зона                        */}
                    {/* ════════════════════════════════════════════ */}
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                        <Box sx={{
                            border: '1px solid rgba(239,68,68,0.25)',
                            borderRadius: 'var(--joy-radius-xl)',
                            overflow: 'hidden',
                        }}>
                            {/* Заголовок */}
                            <Box sx={{
                                px: 2.5, py: 1.5,
                                bgcolor: 'rgba(239,68,68,0.06)',
                                borderBottom: '1px solid rgba(239,68,68,0.15)',
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#ef4444' }}>
                                    Опасная зона
                                </Typography>
                            </Box>

                            {/* Очистка данных за месяц */}
                            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                                    Очистка данных за месяц
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2, lineHeight: 1.6 }}>
                                    Удалить все чеки, премии, ежедневные премии, штрафы и оклады администраторов за выбранный месяц.
                                    Это действие необратимо.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <Box sx={{ flex: 1, minWidth: 180 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Месяц
                                        </Typography>
                                        <Select
                                            value={clearMonth}
                                            onChange={(_, v) => setClearMonth(v || '')}
                                            size="sm"
                                            disabled={monthsLoading || monthOptions.length === 0}
                                            placeholder={
                                                monthsLoading ? 'Загрузка…'
                                                    : monthOptions.length === 0 ? 'Нет данных за месяцы'
                                                    : 'Выбери месяц'
                                            }
                                            {...(monthsLoading ? { startDecorator: <CircularProgress size="sm" /> } : {})}
                                        >
                                            {monthOptions.map(m => (
                                                <Option key={m.value} value={m.value}>{m.label}</Option>
                                            ))}
                                        </Select>
                                    </Box>
                                    {canClear && (
                                        <Button
                                            size="sm"
                                            color="danger"
                                            disabled={!clearMonth}
                                            startDecorator={<DeleteForeverRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => { setClearConfirmText(''); setClearConfirmOpen(true); }}
                                            sx={{ fontWeight: 700, fontSize: 13, flexShrink: 0 }}
                                        >
                                            Очистить
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </motion.div>

                </Stack>
            </Box>


            {/* ═══════ Модалка подтверждения очистки ═══════ */}
            <Modal open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
                <ModalDialog sx={{
                    maxWidth: 420, width: '100%',
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 2.5, sm: 3 },
                    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                }}>
                    {/* Иконка */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '50%',
                            bgcolor: 'rgba(239,68,68,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mx: 'auto', mb: 1.5,
                        }}>
                            <DeleteForeverRoundedIcon sx={{ fontSize: 28, color: '#ef4444' }} />
                        </Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                            Подтверди очистку
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.5, lineHeight: 1.6 }}>
                            Все финансовые данные за <strong style={{ color: '#ef4444' }}>{selectedMonthLabel}</strong> будут
                            безвозвратно удалены: чеки, премии, штрафы и оклады.
                        </Typography>
                    </Box>

                    {/* Поле подтверждения */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                            Введи <strong style={{ color: '#ef4444' }}>{selectedMonthLabel}</strong> для подтверждения
                        </Typography>
                        <Input
                            size="sm"
                            placeholder={selectedMonthLabel}
                            value={clearConfirmText}
                            onChange={(e) => setClearConfirmText(e.target.value)}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            fullWidth
                            color="danger"
                            loading={clearing}
                            disabled={!confirmMatch || !canClear}
                            onClick={handleClear}
                            sx={{ fontWeight: 700 }}
                        >
                            Удалить все данные за {selectedMonthLabel}
                        </Button>
                        <Button size="sm" variant="plain" color="neutral"
                            onClick={() => setClearConfirmOpen(false)}
                            sx={{ flexShrink: 0 }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}